import React, { useState, useEffect } from 'react';
import { ChartCard } from './ChartCard';
import { Place } from '@/types';
import { ProcessedMetrics } from '@/types';
import { formatPlaceName } from '@/lib/utils';
import { calculateMedianHousingAge } from '@/utils/metrics';
import { ChartRowProvider } from '../contexts/ChartRowContext';

interface ChartsSectionProps {
  selectedPlace: Place;
  selectedPlaceMetrics: ProcessedMetrics[];
  peerMetrics: { peer: Place; data: ProcessedMetrics[] }[];
  loading: boolean;
  error: string | null;
}

export const ChartsSection: React.FC<ChartsSectionProps> = ({
  selectedPlace,
  selectedPlaceMetrics,
  peerMetrics,
  loading,
  error
}) => {
  if (loading) {
    return (
      <section className="w-full bg-[#F5F5F5]">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 py-12 sm:py-16 md:py-20">
          <div className="text-center">Loading metrics...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full bg-[#F5F5F5]">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 py-12 sm:py-16 md:py-20">
          <div className="text-center text-red-500">Error: {error}</div>
        </div>
      </section>
    );
  }

  if (!selectedPlaceMetrics?.length) {
    return (
      <section className="w-full bg-[#F5F5F5]">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 py-12 sm:py-16 md:py-20">
          <div className="text-center">No metrics data available.</div>
        </div>
      </section>
    );
  }

  const latestMetrics = selectedPlaceMetrics[0];
  const formattedPlaceName = formatPlaceName(selectedPlace.name);

  // Helper function to safely get a number value
  const safeNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    }
    if (value && typeof value === 'object') {
      if ('value' in value) return safeNumber(value.value);
      if ('growth' in value) {
        // Handle null growth values
        const growth = value.growth;
        return growth === null ? 0 : safeNumber(growth);
      }
    }
    return 0;
  };

  // Helper function to create chart data with consistent formatting
  const createChartData = (
    metricKey: keyof ProcessedMetrics,
    valueFormatter: (value: number) => string = (v) => v.toString(),
    percentageScaler: (value: number) => number = (v) => v,
    useGrowth: boolean = false
  ) => {
    const allData = [
      {
        name: formattedPlaceName,
        value: valueFormatter(safeNumber(useGrowth ? 
          (latestMetrics[metricKey] as any)?.growth : 
          (latestMetrics[metricKey] as any)?.value || latestMetrics[metricKey]
        )),
        percentage: percentageScaler(safeNumber(useGrowth ? 
          (latestMetrics[metricKey] as any)?.growth : 
          (latestMetrics[metricKey] as any)?.value || latestMetrics[metricKey]
        )),
        color: '#CA0079'
      },
      ...peerMetrics
        .filter(({ data }) => data?.length > 0)
        .map(({ peer, data }) => {
          let value;
          let percentage;
          
          if (metricKey === 'median_housing_age') {
            // For median housing age, calculate it using the utility function
            value = calculateMedianHousingAge(data[0]);
            percentage = value;
          } else {
            value = safeNumber(useGrowth ? 
              (data[0][metricKey] as any)?.growth : 
              (data[0][metricKey] as any)?.value || data[0][metricKey]
            );
            percentage = percentageScaler(value);
          }
          
          return {
            name: formatPlaceName(peer.name),
            value: valueFormatter(value),
            percentage,
            color: '#F3BAD3'
          };
        })
    ];

    return allData.sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
  };

  const chartCards = [
    {
      category: "POPULATION",
      title: "Population Growth",
      subtitle: `Annual population growth rate from ${latestMetrics.year - 1} to ${latestMetrics.year}`,
      mainValue: `${(safeNumber(latestMetrics.population_growth) * 100).toFixed(2)}%`,
      mainLocation: formattedPlaceName,
      rank: `Rank #${createChartData('population_growth', v => (v * 100).toFixed(2), v => v * 100).findIndex(d => d.name === formattedPlaceName) + 1}`,
      data: createChartData(
        'population_growth',
        v => `${(v * 100).toFixed(2)}%`,
        v => v * 100
      ),
      xAxisLabel: "% YoY",
      yAxisLabels: ['-1%', '0%', '1%', '2%', '3%'],
    },
    {
      category: "PERMITTING",
      title: "Newly Permitted Units",
      subtitle: `Newly permitted housing units per 1,000 residents in ${latestMetrics.year}`,
      mainValue: `${safeNumber(latestMetrics.permits_metrics?.value / (latestMetrics.total_population / 1000)).toFixed(2)} units`,
      mainLocation: formattedPlaceName,
      rank: `Rank #${createChartData('permits_metrics', v => (v / (latestMetrics.total_population / 1000)).toFixed(2), v => v / (latestMetrics.total_population / 1000)).findIndex(d => d.name === formattedPlaceName) + 1}`,
      data: createChartData(
        'permits_metrics',
        v => (v / (latestMetrics.total_population / 1000)).toFixed(2),
        v => v / (latestMetrics.total_population / 1000)
      ),
      xAxisLabel: "newly permitted units per 1000 residents",
      yAxisLabels: ['0', '1', '2', '3', '4'],
    },
    {
      category: "PERMITTING",
      title: "Housing Stock Growth",
      subtitle: `Percentage increase in total housing units if all permits were built instantly as of ${latestMetrics.year}`,
      mainValue: `${safeNumber(latestMetrics.housing_stock_growth_if_permits_built_instantly).toFixed(1)}%`,
      mainLocation: formattedPlaceName,
      rank: `Rank #${createChartData('housing_stock_growth_if_permits_built_instantly', v => v.toFixed(1), v => v).findIndex(d => d.name === formattedPlaceName) + 1}`,
      data: createChartData(
        'housing_stock_growth_if_permits_built_instantly',
        v => `${v.toFixed(1)}%`,
        v => v
      ),
      xAxisLabel: "% growth in units",
      yAxisLabels: ['0%', '0.5%', '1.0%', '1.5%', '2.0%'],
    },
    {
      category: "HOUSING STOCK",
      title: "Housing Per Capita",
      subtitle: `Housing units per 1,000 residents in ${latestMetrics.year}`,
      mainValue: `${safeNumber(latestMetrics.housing_units_metrics?.value).toFixed(0)} units`,
      mainLocation: formattedPlaceName,
      rank: `Rank #${createChartData('housing_units_metrics', v => v.toFixed(0), v => v).findIndex(d => d.name === formattedPlaceName) + 1}`,
      data: createChartData(
        'housing_units_metrics',
        v => v.toFixed(0),
        v => v
      ),
      xAxisLabel: "housing units per 1000 residents",
      yAxisLabels: ['0', '150', '300', '450', '600'],
    },
    {
      category: "HOUSING STOCK",
      title: "Median Housing Age",
      subtitle: `Median age of the housing stock based on year of construction, data from ${latestMetrics.year}`,
      mainValue: `${safeNumber(latestMetrics.median_housing_age).toFixed(0)} years`,
      mainLocation: formattedPlaceName,
      rank: `Rank #${createChartData('median_housing_age', v => v.toFixed(0), v => v).findIndex(d => d.name === formattedPlaceName) + 1}`,
      data: createChartData(
        'median_housing_age',
        v => `${v.toFixed(0)} years`,
        v => v
      ),
      xAxisLabel: "years old",
      yAxisLabels: ['0', '10', '20', '30', '40'],
    },
    {
      category: "HOUSING STOCK",
      title: "Multi-Family Share",
      subtitle: `Percentage of housing units in multi-family structures for ${latestMetrics.year}`,
      mainValue: `${safeNumber(latestMetrics.pct_mf_existing).toFixed(1)}%`,
      mainLocation: formattedPlaceName,
      rank: `Rank #${createChartData('pct_mf_existing', v => v.toFixed(1), v => v).findIndex(d => d.name === formattedPlaceName) + 1}`,
      data: createChartData(
        'pct_mf_existing',
        v => `${v.toFixed(1)}%`,
        v => v
      ),
      xAxisLabel: "% of units in multi-family structures",
      yAxisLabels: ['0%', '25%', '50%', '75%', '100%'],
    },
    {
      category: "AFFORDABILITY",
      title: "Price to Income Ratio",
      subtitle: `Ratio of median home value to median household income for ${latestMetrics.year}`,
      mainValue: `${safeNumber(latestMetrics.price_to_income).toFixed(1)}x`,
      mainLocation: formattedPlaceName,
      rank: `Rank #${createChartData('price_to_income', v => v.toFixed(1), v => v).findIndex(d => d.name === formattedPlaceName) + 1}`,
      data: createChartData(
        'price_to_income',
        v => `${v.toFixed(1)}x`,
        v => v
      ),
      xAxisLabel: "price to income ratio",
      yAxisLabels: ['0', '2', '4', '6', '8'],
    },
    {
      category: "AFFORDABILITY",
      title: "Rental Burden Share",
      subtitle: `Percentage of renters who were overburdened in ${latestMetrics.year}`,
      mainValue: `${safeNumber(latestMetrics.rent_burden_share).toFixed(1)}%`,
      mainLocation: formattedPlaceName,
      rank: `Rank #${createChartData('rent_burden_share', v => v.toFixed(1), v => v).findIndex(d => d.name === formattedPlaceName) + 1}`,
      data: createChartData(
        'rent_burden_share',
        v => `${v.toFixed(1)}%`,
        v => v
      ),
      xAxisLabel: "% of renters paying >= 30% of income as rent",
      yAxisLabels: ['0%', '15%', '30%', '45%', '60%'],
    },
    {
      category: "AFFORDABILITY",
      title: "Rental Vacancy Rate",
      subtitle: `Percentage of rental units that are vacant or for rent in ${latestMetrics.year}`,
      mainValue: `${safeNumber(latestMetrics.rental_vacancy_rate).toFixed(1)}%`,
      mainLocation: formattedPlaceName,
      rank: `Rank #${createChartData('rental_vacancy_rate', v => v.toFixed(1), v => v).findIndex(d => d.name === formattedPlaceName) + 1}`,
      data: createChartData(
        'rental_vacancy_rate',
        v => `${v.toFixed(1)}%`,
        v => v
      ),
      xAxisLabel: "% of rental units vacant",
      yAxisLabels: ['0%', '2%', '4%', '6%', '8%'],
    },
  ];

  return (
    <section className="w-full bg-[#F5F5F5]">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 py-12 sm:py-16 md:py-20">
        <ChartRowProvider>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
            {chartCards.map((chart, cardIndex) => (
              <ChartCard
                key={chart.title}
                rowIndex={0}
                cardIndex={cardIndex}
                {...chart}
              />
            ))}
          </div>
        </ChartRowProvider>
      </div>
    </section>
  );
};
